import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ApplicationService } from '../application.service';
import { Application, EmiOption, RecommendedAndOtherEMIPlans, MerchantDetails } from '../application';
import { LoginService } from '../../login/login.service';
import { HeaderService, Header } from '../../common/header/header.service';
import { FooterService, Footer } from '../../common/footer/footer.service';
import { LoaderService, Loader } from '../../common/loader/loader.service';
import { AwsTrackingService } from '../../common/tracking/aws-tracking.service';
import { EventDetails } from '../../common/tracking/aws-tracker';
import { environment } from '../../../environments/environment';
import { TokenService } from 'app/token/token.service';

declare var $: any;

@Component({
  selector: 'zest-select-emi',
  templateUrl: './select-emi.component.html',
  styleUrls: ['./select-emi.component.scss']
})
export class SelectEmiComponent implements OnInit {

  public recommendedAndOtherEMIPlans: RecommendedAndOtherEMIPlans;
  public emiOption: EmiOption;
  public recommendedOptions: EmiOption[];
  public otherOptions: EmiOption[];
  public application: Application;
  public processing = false;
  public showOfferRibbon = false;
  public ribbonOfferText = '';
  public merchantDetails: MerchantDetails;
  public aprModalContent;
  public selectedOption;
  public bulletText;
  public footerNote;
  public showAPR = false;

  constructor(private router: Router, private route: ActivatedRoute, private headerService: HeaderService, private awsTrackingService: AwsTrackingService, private footerService: FooterService, private loaderService: LoaderService, private applicationService: ApplicationService, private loginService: LoginService, private tokenService: TokenService) { }

  ngOnInit() {
    this.aprModalContent = this.tokenService.getAPRContent();
    this.applicationService.getAPRSwitch().subscribe(res => {
      this.showAPR = res.showAPR;
    });
    this.application = this.applicationService.getApplication();
    if (this.application && this.application.BasketItems && this.application.BasketItems.length > 0 && this.application.BasketItems[0].MerchantItemId && this.application.BasketItems[0].Description) {
      this.merchantDetails = {
        MerchantItemId: this.application.BasketItems[0].MerchantItemId,
        Description: this.application.BasketItems[0].Description
      };
    }
    this.route.queryParams.subscribe(value => {
      if (value.SessionId) {
        this.tokenService.updateAndSetGlobalParams('SessionId', value.SessionId);
      }
      if (value.skipToLogin || (this.application && this.application.CustomerId && this.application.CustomerId.length > 0 && this.application.CustomerId !== environment.dummyUuid)) {
        const event = new EventDetails('EMI-Page-Skipped', 'page', (value.skipToLogin ? 'skipToLogin' : 'alreadyAssigned'));
        this.awsTrackingService.sendDataToFunnel(event).subscribe();
        this.loaderService.ShowLoaderSource.next(new Loader(true, ''));
        this.loginService.goToLoginUrl(true);
      } else {
        this.initializePage();
      }
      if (value.hideGmail) {
        this.tokenService.setHideGmail(true);
      }
      if (value.isOfflineGCAgent) {
        this.tokenService.setOfflineGcAgent(true);
      }
    }, error => {
      this.initializePage();
    });
  }
  initializePage() {
    const event = new EventDetails('EMI-Page-Loaded', 'page', 'triggered');
    this.awsTrackingService.sendDataToFunnel(event).subscribe();
    this.awsTrackingService.gtmPageLoadEvent('EMI-Page-Loaded');
    this.recommendedAndOtherEMIPlans = this.route.snapshot.data['recommendedAndOtherEMIPlans'];
    if (this.recommendedAndOtherEMIPlans) {
      this.getEmiOptions();
    }
    this.headerService.ShowHeaderSource.next(new Header(3, false, 0, false, '', false));
    this.footerService.ShowFooterSource.next(new Footer(true, false, false, true, true, true));
  }
  getEmiOptions() {
    const allEmiOptions: EmiOption[] = [...(this.recommendedAndOtherEMIPlans.RecommendedEmiOptions || []),
    ...(this.recommendedAndOtherEMIPlans.OtherEmiOptions || [])];
    allEmiOptions.sort((a, b) => {
      return a.NumberOfMonths - b.NumberOfMonths;
    });
    if (allEmiOptions.length) {
      this.recommendedOptions = allEmiOptions.slice(0, 3);
      this.otherOptions = allEmiOptions.slice(3);
    } else {
      this.recommendedOptions = [];
      this.otherOptions = [];
    }
    this.selectDefaultRecommendedOption();
    this.getOffers();
  }
  selectDefaultRecommendedOption() {
    let defaultSelected = false;
    this.recommendedOptions.forEach(option => {
      if (option.IsDefault) {
        defaultSelected = true;
        this.selectEmi(option, defaultSelected);
        return true;
      }
    });
    if (!defaultSelected) {
      this.selectEmi(this.recommendedOptions[0]);
    }
    if (this.emiOption === null) {
      this.recommendedOptions[0].IsDefault = true;
      this.application.InterestAmount = this.recommendedOptions[0].InterestAmount;
      this.selectEmi(this.recommendedOptions[0]);
    }
  }
  getOffers() {
    const allTerms = this.otherOptions.concat(this.recommendedOptions);
    const offerEmiPlans = allTerms.filter(emiOption => (emiOption.InterestRate === 0));
    if (offerEmiPlans.length > 0) {
      this.ribbonOfferText = '0% Interest on ';
      let offer = '';
      offerEmiPlans.sort((a: EmiOption, b: EmiOption) => {
        return a.NumberOfMonths - b.NumberOfMonths;
      });
      offerEmiPlans.forEach((emiOption, index) => {
        offer += emiOption.NumberOfMonths + ', ';
        if (index === (offerEmiPlans.length - 2)) {
          offer = offer.substring(0, (offer.length - 2));
          offer += ' &amp; ';
        }
        if (index === (offerEmiPlans.length - 1)) {
          offer = offer.substring(0, (offer.length - 2));
        }
      });
      this.ribbonOfferText += offer + ' months EMIs';
      this.showOfferRibbon = true;
    }
  }
  selectEmi(option: EmiOption, defaultSelected = false) {
    const event = new EventDetails(`EMI-Tenure-Selected${defaultSelected ? '-default' : ''}-${option.NumberOfMonths}`, 'event', 'triggered');
    this.awsTrackingService.sendDataToFunnel(event).subscribe();
    this.emiOption = option;
    this.application.InterestAmount = this.emiOption.InterestAmount;
  }
  confirmEMI() {
    this.loaderService.ShowLoaderSource.next(new Loader(true, ''));
    this.processing = true;
    const application = this.applicationService.getApplication();
    application.NumberOfInstallments = this.emiOption.NumberOfMonths;
    application.TotalInstallmentAmount = this.emiOption.TotalMonthlyAmount;
    this.applicationService.saveApplication(application);
    this.applicationService.saveSelectedEmi(this.application.ApplicationId, this.emiOption.NumberOfMonths, this.application.DownpaymentAmount).subscribe(Response => {
      this.awsTrackingService.gtmTriggerEvent('EMI-Selected');
      const event = new EventDetails('EMI-Selected', 'event', 'triggered');
      this.awsTrackingService.sendDataToFunnel(event).subscribe();
      this.applicationService.goToNextRoute(this.router.url);
    }, error => {
      this.loaderService.ShowLoaderSource.next(new Loader(false, ''));
      this.processing = false;
    });
  }

  openAPRModal(selectedOption) {
    $('#aprModal').modal('open');
    setTimeout(() => {
      document.getElementById('modalHeader').scrollIntoView();
    }, 100);
    this.selectedOption = selectedOption;
    if (!this.aprModalContent) {
      this.applicationService.getAPRContent().subscribe(res => {
        this.aprModalContent = res;
        this.tokenService.setAPRContent(res);
        this.bulletText = this.aprModalContent.bulletPoint1.replace(' {interestAmount}.', '') + ` <b>${'₹' + this.selectedOption.InterestAmount}</b>`;
        this.footerNote = '<b>Note: </b>' + this.aprModalContent.description5;
      });
    } else {
      this.bulletText = this.aprModalContent.bulletPoint1.replace(' {interestAmount}.', '') + ` <b>${'₹' + this.selectedOption.InterestAmount}</b>`;
      this.footerNote = '<b>Note: </b>' + this.aprModalContent.description5;
    }
  }

  dismissModal() {
    $('#aprModal').modal('close');
  }
}
